import { memo, useState, useCallback, useEffect, useMemo } from "react"
import { useNavigate, useLocation, useSearchParams } from "react-router-dom"
import { useTranslation } from 'react-i18next'
import { X, Pencil, Check, Tag, Plus } from "lucide-react"
import type { PortfolioItem, Artist } from "@/types/models"
import { SaveButton } from "./SaveButton"
import { DeleteConfirmationModal } from "@/components/shared/DeleteConfirmationModal"
import { useAnalytics } from "@/hooks/useAnalytics"
import { getDiscountInfo, formatDiscountBadge, formatPriceWithDiscount } from "@/lib/discountUtils"
import { CountryFlag } from "@/lib/countryFlags"
import { DiscountSheet } from "@/components/artist/DiscountSheet"
import { useImageLoadAndVisible } from "@/hooks/useImageLoadAndVisible"
import { isNewFlashByCreatedAt } from "@/lib/flashDisplayUtils"

interface FlashCardProps {
  item: PortfolioItem
  artist?: Artist
  onImageLoad?: () => void
  preloaded?: boolean
  searchScore?: number
  editMode?: boolean
  onDelete?: (itemId: string) => void
  onMetadataChange?: (itemId: string, updates: { minPrice?: string; minSize?: string; booked?: boolean }) => void
  onMetadataSave?: (itemId: string, updates?: { minPrice?: string; minSize?: string; booked?: boolean }) => Promise<void>
  showCountryFlag?: boolean
  onRefreshFlashes?: () => void
  showAddToCollection?: boolean
  onAddToCollection?: (itemId: string) => Promise<void>
  analyticsSource?: string
  feedType?: string
  selectionMode?: boolean
  isSelected?: boolean
  canShowSelectionIndicator?: boolean
  onToggleSelect?: () => void
  onDetailClick?: (itemId: string) => void
  hideSavePill?: boolean
}

export const FlashCard = memo(function FlashCard({ 
  item, 
  artist, 
  onImageLoad,
  preloaded = false,
  searchScore,
  editMode = false,
  onDelete,
  onMetadataChange,
  onMetadataSave,
  showCountryFlag = false,
  onRefreshFlashes,
  showAddToCollection = false,
  onAddToCollection,
  analyticsSource,
  feedType,
  selectionMode = false,
  isSelected = false,
  canShowSelectionIndicator = true,
  onToggleSelect,
  onDetailClick,
  hideSavePill = false
}: FlashCardProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()
  const { trackEvent } = useAnalytics()
  const [showDiscountSheet, setShowDiscountSheet] = useState(false)
  
  // Edit mode state
  const [editingPrice, setEditingPrice] = useState(false)
  const [editingSize, setEditingSize] = useState(false)
  const [localPrice, setLocalPrice] = useState(item.minPrice || '')
  const [localSize, setLocalSize] = useState(item.minSize || '')
  const [savingPrice, setSavingPrice] = useState(false)
  const [savingSize, setSavingSize] = useState(false)
  const [localBooked, setLocalBooked] = useState(Boolean(item.booked))
  const [savingBooked, setSavingBooked] = useState(false)
  const [addingToCollection, setAddingToCollection] = useState(false)

  useEffect(() => {
    setLocalPrice(item.minPrice || '')
    setLocalSize(item.minSize || '')
  }, [item.minPrice, item.minSize])

  useEffect(() => {
    setLocalBooked(Boolean(item.booked))
  }, [item.booked])

  // Use the reusable hook for image load and visibility detection
  const { imageVisible, imgRef, handleImageLoad, handleImageError } = useImageLoadAndVisible({
    onImageLoadAndVisible: onImageLoad,
    preloaded
  })

  const handleAddToCollectionClick = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!onAddToCollection || addingToCollection) return
    
    setAddingToCollection(true)
    try {
      await onAddToCollection(item.id)
      // Keep the check icon visible for a moment to show success
      setTimeout(() => {
        setAddingToCollection(false)
      }, 1500)
    } catch (error) {
      console.error('Error adding to collection:', error)
      setAddingToCollection(false)
    }
  }, [onAddToCollection, item.id, addingToCollection])

  const isDiscoverFeedRoute =
    location.pathname === '/feed' || location.pathname === '/discover'

  const resolvedSource = analyticsSource || (
    isDiscoverFeedRoute
      ? 'feed'
      : location.pathname.startsWith('/artist/')
      ? 'artist_page'
      : location.pathname.startsWith('/profile/collection')
      ? 'collection'
      : 'unknown'
  )

  const handleCardClick = useCallback(() => {
    if (selectionMode && onToggleSelect) {
      onToggleSelect()
      return
    }
    if (!editMode) {
      trackEvent('item_clicked', {
        item_id: item.id,
        item_type: 'flash',
        artist_id: item.artistId,
        source: resolvedSource,
        feed_type: feedType
      })
      
      // If a detail click handler is provided, use it instead of navigating
      if (onDetailClick) {
        onDetailClick(item.id)
        return
      }
      
      if (isDiscoverFeedRoute) {
        const newParams = new URLSearchParams(searchParams)
        newParams.set('flash', item.id)
        setSearchParams(newParams)
      } else {
        const route = `/flashes/${item.id}`
        if (location.pathname.startsWith('/profile/collection')) {
          navigate(route, {
            state: {
              from: {
                pathname: location.pathname,
                search: location.search,
                hash: location.hash
              },
              scrollPosition: window.scrollY
            }
          })
        } else {
          navigate(route)
        }
      }
    }
  }, [editMode, feedType, item.artistId, item.id, location.hash, location.pathname, location.search, navigate, resolvedSource, searchParams, setSearchParams, trackEvent, selectionMode, onToggleSelect, onDetailClick])

  const handleArtistClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation() // Prevent card click
    if (artist?.id && !editMode) {
      navigate(`/artist/${artist.id}`)
    }
  }, [navigate, artist?.id, editMode])

  const handleDelete = useCallback(() => {
    onDelete?.(item.id)
  }, [onDelete, item.id])

  const handlePriceEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (!editMode) return
    setEditingPrice(true)
  }, [editMode])

  const handleSizeEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (!editMode) return
    setEditingSize(true)
  }, [editMode])

  const handlePriceSave = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation()
    console.log('[FlashCard] Price save clicked:', {
      flashId: item.id,
      oldPrice: item.minPrice,
      newPrice: localPrice
    })
    
    if (!localPrice || localPrice === item.minPrice) {
      console.log('[FlashCard] No price change, skipping save')
      setEditingPrice(false)
      return
    }
    
    setSavingPrice(true)
    try {
      // Save directly with the changes - don't rely on state updates
      if (onMetadataSave) {
        console.log('[FlashCard] Saving price directly to Firestore')
        await onMetadataSave(item.id, { minPrice: localPrice })
        console.log('[FlashCard] Price saved successfully')
      }
      setEditingPrice(false)
    } catch (error) {
      console.error('[FlashCard] Failed to save price:', error)
    } finally {
      setSavingPrice(false)
    }
  }, [item.id, item.minPrice, localPrice, onMetadataSave])

  const handleSizeSave = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation()
    console.log('[FlashCard] Size save clicked:', {
      flashId: item.id,
      oldSize: item.minSize,
      newSize: localSize
    })
    
    if (!localSize || localSize === item.minSize) {
      console.log('[FlashCard] No size change, skipping save')
      setEditingSize(false)
      return
    }
    
    setSavingSize(true)
    try {
      // Save directly with the changes - don't rely on state updates
      if (onMetadataSave) {
        console.log('[FlashCard] Saving size directly to Firestore')
        await onMetadataSave(item.id, { minSize: localSize })
        console.log('[FlashCard] Size saved successfully')
      }
      setEditingSize(false)
    } catch (error) {
      console.error('[FlashCard] Failed to save size:', error)
    } finally {
      setSavingSize(false)
    }
  }, [item.id, item.minSize, localSize, onMetadataSave])

  const handlePriceChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    const numbersOnly = e.target.value.replace(/\D/g, '')
    const limited = numbersOnly.slice(0, 4)
    setLocalPrice(limited)
    // Track the change for batch save (but don't save yet)
    if (limited !== item.minPrice) {
      onMetadataChange?.(item.id, { minPrice: limited })
    }
  }, [item.id, item.minPrice, onMetadataChange])

  const handleSizeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    const numbersOnly = e.target.value.replace(/\D/g, '')
    const limited = numbersOnly.slice(0, 3)
    setLocalSize(limited)
    // Track the change for batch save (but don't save yet)
    if (limited !== item.minSize) {
      onMetadataChange?.(item.id, { minSize: limited })
    }
  }, [item.id, item.minSize, onMetadataChange])

  const handleBookingToggle = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation()

    if (!editMode || !onMetadataSave) {
      return
    }

    const previousBookedState = localBooked
    const nextBookedState = !previousBookedState

    console.log('[FlashCard] Toggling booked state:', {
      flashId: item.id,
      previous: previousBookedState,
      next: nextBookedState
    })

    setLocalBooked(nextBookedState)
    setSavingBooked(true)

    try {
      await onMetadataSave(item.id, { booked: nextBookedState })
      console.log('[FlashCard] Booked state updated successfully')
    } catch (error) {
      console.error('[FlashCard] Failed to toggle booked state:', error)
      setLocalBooked(previousBookedState)
    } finally {
      setSavingBooked(false)
    }
  }, [editMode, onMetadataSave, item.id, localBooked])

  const handleDiscountChanged = useCallback(() => {
    console.log('[FlashCard] Discount changed, refreshing flash data')
    onRefreshFlashes?.()
  }, [onRefreshFlashes])
  
  // Get discount information
  const discountInfo = getDiscountInfo(item)
  const hasActiveDiscount = discountInfo?.isActive

  const showNewFlashTag = useMemo(
    () => !editMode && isNewFlashByCreatedAt(item.createdAt),
    [editMode, item.createdAt]
  )

  return (
    <div className="flex flex-col">
      <div 
        className={`relative w-full group ${editMode ? 'cursor-default' : 'cursor-pointer'}`} 
        onClick={handleCardClick}
        style={item.width && item.height ? { aspectRatio: `${item.width}/${item.height}` } : undefined}
      >
        {/* Skeleton stays visible until image is fully visible */}
        <div className={`absolute inset-0 w-full bg-muted rounded-[var(--card-radius)] transition-opacity duration-300 ${
          preloaded || imageVisible ? 'opacity-0 pointer-events-none' : 'opacity-100 animate-pulse'
        }`} />
        
        <img
          ref={imgRef}
          src={item.compressedImageUrl || item.imageUrl}
          alt={item.caption || t('common.flashTattooDesign')}
          className={`relative w-full h-auto object-cover rounded-[var(--card-radius)] block shadow-lg transition-opacity duration-500 ease-out ${
            preloaded || imageVisible ? 'opacity-100' : 'opacity-0'
          }`}
          loading="eager"
          onLoad={preloaded ? undefined : handleImageLoad}
          onError={preloaded ? undefined : handleImageError}
        />
        {/* Primary color overlay */}
        <div className="absolute inset-0 bg-primary/5 rounded-[var(--card-radius)]" />
        {/* Red overlay for exclusive flashes */}
        {item.accessCode && (
          <div className="absolute inset-0 bg-red-500/20 rounded-[var(--card-radius)] flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-20 h-20 text-red-700/60">
              <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clipRule="evenodd" />
            </svg>
          </div>
        )}
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-[var(--card-radius)]" />
        
        {/* Dev mode: Search score overlay */}
        {import.meta.env.DEV && searchScore !== undefined && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <span className="text-red-600 font-bold text-sm bg-white/90 px-2 py-1 rounded shadow-lg">
              {searchScore.toFixed(3)}
            </span>
          </div>
        )}
        
        {/* Delete button - Top Right (Edit Mode) */}
        {editMode && onDelete && (
          <DeleteConfirmationModal
            item={item}
            type="flash"
            onConfirm={handleDelete}
          >
            <div 
              className="absolute top-2 right-2 bg-background text-foreground w-7 h-7 rounded-full flex items-center justify-center cursor-pointer hover:bg-background/80 transition-all duration-300 shadow-lg animate-in fade-in-0 zoom-in-50 duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </div>
          </DeleteConfirmationModal>
        )}
        
        {/* Booking status pill (edit mode) or discount badges (view mode) */}
        {editMode ? (
          <button
            type="button"
            onClick={handleBookingToggle}
            disabled={savingBooked}
            className={`absolute top-2 left-2 rounded-full px-3 py-1 text-xs font-semibold shadow-sm transition-all duration-300 focus:outline-none focus:ring-0 flex items-center gap-2 ${
              localBooked
                ? 'bg-red-500 text-white hover:bg-red-500/90 focus:ring-red-500'
                : 'bg-background text-foreground hover:bg-background/80 focus:ring-foreground'
            } ${savingBooked ? 'opacity-80 cursor-wait' : 'cursor-pointer'}`}
          >
            <span>{savingBooked ? t('common.saving') : localBooked ? t('booking.booked') : t('booking.booked')}</span>
            <span
              className={`inline-flex h-4 w-8 items-center rounded-full px-1 transition-all duration-300 ${
                localBooked ? 'bg-white/40' : 'bg-foreground/20'
              }`}
            >
              <span
                className={`h-3 w-3 rounded-full bg-foreground shadow transition-all duration-300 ${
                  localBooked ? 'translate-x-3' : 'translate-x-0'
                }`}
              />
            </span>
          </button>
        ) : (
          <>
            {hasActiveDiscount && discountInfo && (
              <div 
                className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 transition-all duration-300"
              >
                <span>{formatDiscountBadge(discountInfo)}</span>
              </div>
            )}
            {showNewFlashTag && !hasActiveDiscount && (
              <div className="absolute top-2 left-2 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 bg-[var(--save-active)] text-white shadow-sm transition-all duration-300">
                <span>NEW</span>
              </div>
            )}
          </>
        )}
        
        {/* Size pill - Shows at top in normal mode, bottom in edit mode; hide top-left when discount or NEW */}
        {(editMode || (item.minSize && !hasActiveDiscount && !showNewFlashTag)) && (
          <div 
            className={`absolute left-2 text-xs font-medium rounded-full flex items-center transition-all duration-300 ${
              editMode ? 'bottom-11 animate-in slide-in-from-bottom-2 bg-primary text-primary-foreground px-2 py-1' : 'top-2 bg-background text-foreground px-2 py-1'
            } ${editingSize ? 'px-1 py-1' : ''}`}
            onClick={editMode && !editingSize ? handleSizeEdit : undefined}
          >
            {editMode && editingSize ? (
              <>
                <input
                  type="text"
                  inputMode="numeric"
                  value={localSize}
                  onChange={handleSizeChange}
                  onClick={(e) => e.stopPropagation()}
                  className="w-12 bg-primary text-primary-foreground text-xs text-center outline-none border-b border-white px-1 underline"
                  placeholder="0"
                  maxLength={3}
                  autoFocus
                />
                <span className="text-xs px-1">cm</span>
                <button
                  onClick={handleSizeSave}
                  disabled={savingSize}
                  className="ml-1 p-0.5 hover:bg-primary-foreground/10 rounded transition-colors"
                >
                  <Check className={`w-3 h-3 text-primary-foreground ${savingSize ? 'animate-pulse' : ''}`} />
                </button>
              </>
            ) : (
              <>
                <span>~{localSize || item.minSize || '?'}cm</span>
                {editMode && (
                  <Pencil className="w-3 h-3 ml-1 text-primary-foreground" />
                )}
              </>
            )}
          </div>
        )}
        
        {/* Price pill - Positioned based on edit mode */}
        {/* Show discount percentage in edit mode when discount is active, otherwise show price */}
        {editMode && hasActiveDiscount && discountInfo ? (
          <div 
            className="absolute right-2 bottom-11 animate-in slide-in-from-top-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center transition-all duration-300"
          >
            <span>{discountInfo.discountPercentage}%</span>
          </div>
        ) : !hasActiveDiscount && (
          <div 
            className={`absolute right-2 text-xs font-medium rounded-full flex items-center transition-all duration-300 ${
              editMode ? 'bottom-11 animate-in slide-in-from-top-2 bg-primary text-primary-foreground px-2 py-1' : 'top-2 bg-background text-foreground px-2 py-1'
            } ${editingPrice ? 'px-1 py-1' : ''}`}
            onClick={editMode && !editingPrice ? handlePriceEdit : undefined}
          >
            {editMode && editingPrice ? (
              <>
                <input
                  type="text"
                  inputMode="numeric"
                  value={localPrice}
                  onChange={handlePriceChange}
                  onClick={(e) => e.stopPropagation()}
                  className="w-14 bg-primary text-primary-foreground text-xs text-center outline-none border-b border-white px-1 underline"
                  placeholder="0"
                  maxLength={4}
                  autoFocus
                />
                <span className="text-xs px-1">€</span>
                <button
                  onClick={handlePriceSave}
                  disabled={savingPrice}
                  className="ml-1 p-0.5 hover:bg-primary-foreground/10 rounded transition-colors"
                >
                  <Check className={`w-3 h-3 text-primary-foreground ${savingPrice ? 'animate-pulse' : ''}`} />
                </button>
              </>
            ) : editMode ? (
              <>
                <span>{localPrice || item.minPrice || '?'}€</span>
                <Pencil className="w-3 h-3 ml-1 text-primary-foreground" />
              </>
            ) : (
              <>
                <span>{t('common.from')} {localPrice || item.minPrice || '?'}€</span>
              </>
            )}
          </div>
        )}
        
        {/* Discounted price display - Top Right */}
        {hasActiveDiscount && discountInfo && (
          <div 
            className={`absolute right-2 bg-background text-foreground text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1 transition-all duration-300 ${
              editMode ? 'bottom-2 animate-in slide-in-from-top-2' : 'top-2'
            }`}
          >
            <span className="text-red-500 font-bold">{formatPriceWithDiscount(discountInfo).currentPrice}</span>
            <span className="line-through text-muted-foreground">{formatPriceWithDiscount(discountInfo).originalPrice}</span>
          </div>
        )}

        {/* Discount button - Bottom Center (Edit Mode Only) */}
        {editMode && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setShowDiscountSheet(true)
            }}
            className={`absolute bottom-2 left-2 right-2 text-xs font-semibold px-2 py-1.5 rounded-full flex items-center justify-center gap-2 transition-all duration-300 focus:outline-none focus:ring-0 ${
              hasActiveDiscount 
                ? 'bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-primary' 
                : 'bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-primary'
            }`}
          >
            <Tag className="w-3 h-3" />
            <span>{hasActiveDiscount ? 'Edit Discount' : 'Discount'}</span>
          </button>
        )}

        {/* Selection circle - Bottom Right (Selection Mode) */}
        {selectionMode && canShowSelectionIndicator && (
          <div 
            className="absolute bottom-2 right-2"
            onClick={(e) => {
              e.stopPropagation()
              onToggleSelect?.()
            }}
          >
            <div 
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all shadow-sm cursor-pointer ${
                isSelected 
                  ? 'bg-primary border-primary' 
                  : 'bg-background/90 border-foreground/30'
              }`}
            >
              {isSelected && <Check className="w-4 h-4 text-primary-foreground" />}
            </div>
          </div>
        )}

        {/* Save pill - Bottom Right (Hidden in Edit Mode and Selection Mode) */}
        {!editMode && !selectionMode && !hideSavePill && (
          <div className="absolute bottom-2 right-2" onClick={(e) => e.stopPropagation()}>
            <div className="bg-primary rounded-full flex items-center overflow-hidden shadow-sm">
              {showAddToCollection ? (
                <button
                  onClick={handleAddToCollectionClick}
                  disabled={addingToCollection}
                  className="text-primary-foreground text-xs font-medium px-2 py-1 flex items-center gap-1 transition-colors hover:bg-primary/80 disabled:opacity-50"
                >
                  {addingToCollection ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                </button>
              ) : (
                  <SaveButton
                    item={item}
                    className="hover:bg-primary/80 text-primary-foreground"
                    source={resolvedSource}
                  />
              )}
            </div>
          </div>
        )}

        {!editMode && showCountryFlag && (
          <div className="absolute bottom-2 left-2">
            <CountryFlag country={artist?.location?.country} showBorder={false} customSizeInPx={20} />
          </div>
        )}

        {!editMode && item.booked && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center rounded-[var(--card-radius)] pointer-events-none bg-red-600/45"
            aria-hidden
          >
            <span className="text-white font-normal text-lg sm:text-xl tracking-[0.25em] drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]">
              BOOKED
            </span>
          </div>
        )}
      </div>
      
      {/* Artist info */}
      {artist && (
        <div className="flex justify-center items-center card-content px-2 bg-background">
          <div 
            className="flex items-center gap-2 cursor-pointer hover:opacity-75 transition-opacity"
            onClick={handleArtistClick}
          >
            {artist?.avatarUrl && (
              <img
                src={artist.avatarUrl}
                alt={artist.displayName}
                className="w-6 h-6 rounded-full object-cover flex-shrink-0"
              />
            )}
            <span className="text-card-artist truncate">
              {artist.displayName || t('common.artistName')}
            </span>
          </div>
        </div>
      )}

      <DiscountSheet
        isOpen={showDiscountSheet}
        onOpenChange={setShowDiscountSheet}
        flash={item}
        artist={artist}
        onDiscountChanged={handleDiscountChanged}
      />
    </div>
  )
})
